import { Controller } from "@hotwired/stimulus"

// Listens for the `uploads:fileUploaded` custom event emitted by
// uploads_controller after the receipt has been uploaded to Active Storage,
// posts the resulting signed_id to the server's expense extract endpoint, and
// fills any blank form inputs with the suggestions returned by the AI extractor.
//
// A receipt can have several pages (one file each). Once no upload is pending, all pages
// are posted in order; the server merges them into one PDF and returns its signed id, which
// this controller submits as the receipt in place of the pages — the saved expense then
// takes over the lines read off that very file. Adding, removing or reordering a page reads
// the receipt again. A photo showing several receipts fills nothing and shows `warning`.
//
// Wiring: `data-controller="expense-receipt-extraction"` on the form, plus
// `data-expense-receipt-extraction-url-value` pointing to the extract endpoint.
// Each prefillable input declares `data-expense-receipt-extraction-target="<field>"`.
export default class extends Controller {
  static values = { url: String }
  static targets = [
    "amount",
    "tipAmount",
    "pfandAmount",
    "quantity",
    "currency",
    "expenseDate",
    "vendorName",
    "description",
    "expenseCategory",
    "bewirtung",
    "receiptInvoiceComplete",
    "indicator",
    "fields",
    "warning",
    "warningMessage",
    "warningAction",
  ]

  static pageEvents = [
    "uploads:fileUploaded",
    "uploads:fileRemoved",
    "uploads:filesReordered",
    "uploads:uploadFailed",
  ]

  connect() {
    this.onFileUploaded = this.onFileUploaded.bind(this)
    this.requestId = 0
    this.constructor.pageEvents.forEach((name) =>
      this.element.addEventListener(name, this.onFileUploaded),
    )
  }

  disconnect() {
    this.constructor.pageEvents.forEach((name) =>
      this.element.removeEventListener(name, this.onFileUploaded),
    )
  }

  async onFileUploaded(event) {
    const detail = event.detail || {}
    if (detail.pending > 0) return

    const signedIds = detail.signedIds ?? (detail.signedId ? [detail.signedId] : [])
    // Every read supersedes the one before: a slower earlier answer must not win.
    const requestId = ++this.requestId
    this.releasePages()
    this.hideWarning()
    if (signedIds.length === 0) return

    this.showIndicator()
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ signed_ids: signedIds }),
      })
      if (!response.ok || requestId !== this.requestId) return

      const payload = await response.json()
      if (requestId !== this.requestId) return
      if (payload.signed_id) this.useMergedReceipt(payload.signed_id, signedIds)
      // The pages could not be merged: saving would fail the same way, so say so now.
      if (payload.receipt_error) {
        this.showWarning(payload.receipt_error, { action: false })
        return
      }
      if (payload.receipt_count > 1) {
        this.showWarning((payload.errors || [])[0])
        return
      }
      this.applySuggestions(payload.suggestions || {})
    } catch (error) {
      console.warn("Receipt extraction failed:", error)
    } finally {
      if (requestId === this.requestId) this.hideIndicator()
    }
  }

  // Submits the merged PDF as the receipt: the page inputs are disabled, not removed, so a
  // later change to the pages can bring them back (#releasePages).
  useMergedReceipt(signedId, pageIds) {
    const pageInputs = this.pageInputs(pageIds)
    if (pageInputs.length === 0) return

    pageInputs.forEach((input) => {
      input.disabled = true
      input.dataset.mergedPage = "true"
    })
    const merged = document.createElement("input")
    merged.type = "hidden"
    merged.name = pageInputs[0].name.replace("[receipt_pages][]", "[receipt]")
    merged.value = signedId
    merged.dataset.mergedReceipt = "true"
    pageInputs[pageInputs.length - 1].after(merged)
  }

  releasePages() {
    this.element.querySelectorAll("input[data-merged-receipt]").forEach((input) => input.remove())
    this.element.querySelectorAll("input[data-merged-page]").forEach((input) => {
      input.disabled = false
      delete input.dataset.mergedPage
    })
  }

  pageInputs(pageIds) {
    return Array.from(this.element.querySelectorAll('input[type="hidden"]')).filter(
      (input) => input.name.endsWith("[receipt_pages][]") && pageIds.includes(input.value),
    )
  }

  showWarning(message, { action = true } = {}) {
    if (!this.hasWarningTarget) return
    if (this.hasWarningMessageTarget) this.warningMessageTarget.textContent = message || ""
    if (this.hasWarningActionTarget) this.warningActionTarget.classList.toggle("hidden", !action)
    this.warningTarget.classList.remove("hidden")
  }

  hideWarning() {
    if (this.hasWarningTarget) this.warningTarget.classList.add("hidden")
  }

  applySuggestions(suggestions) {
    const map = [
      ["amount", this.amountTarget],
      ["tip_amount", this.hasTipAmountTarget ? this.tipAmountTarget : null],
      ["pfand_amount", this.hasPfandAmountTarget ? this.pfandAmountTarget : null],
      ["currency", this.hasCurrencyTarget ? this.currencyTarget : null],
      ["expense_date", this.hasExpenseDateTarget ? this.expenseDateTarget : null],
      ["vendor_name", this.hasVendorNameTarget ? this.vendorNameTarget : null],
      ["description", this.hasDescriptionTarget ? this.descriptionTarget : null],
      ["expense_category_id", this.hasExpenseCategoryTarget ? this.expenseCategoryTarget : null],
    ]
    map.forEach(([key, input]) => {
      if (!input) return
      const value = suggestions[key]
      if (value === null || value === undefined || value === "") return
      if (input.value && input.value.trim() !== "") return // never overwrite the user
      input.value = value
      input.dispatchEvent(new Event("change", { bubbles: true }))
    })

    this.applyQuantitySuggestion(suggestions)
    this.applyBewirtungSuggestions(suggestions)
  }

  // A detected quantity (e.g. litres on a fuel receipt) only fits when its unit is the
  // Mengeneinheit the Menge field currently stands for (published by
  // expense_quantity_controller); otherwise the field stays untouched.
  applyQuantitySuggestion(suggestions) {
    if (!this.hasQuantityTarget) return
    const { quantity, quantity_unit: unit } = suggestions
    if (quantity === null || quantity === undefined || quantity === "") return
    if (this.quantityTarget.value.trim() !== "") return
    if (this.quantityTarget.closest("[data-quantity-unit]")?.dataset.quantityUnit !== unit) return
    this.quantityTarget.value = quantity
    this.quantityTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Bewirtung detections drive checkboxes, not text inputs. Only positively flag a
  // Bewirtung (never uncheck the user's own choice); reveal the section via change.
  applyBewirtungSuggestions(suggestions) {
    if (
      this.hasBewirtungTarget &&
      suggestions.bewirtung === true &&
      !this.bewirtungTarget.checked
    ) {
      this.bewirtungTarget.checked = true
      this.bewirtungTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    if (
      this.hasReceiptInvoiceCompleteTarget &&
      typeof suggestions.receipt_invoice_complete === "boolean"
    ) {
      this.receiptInvoiceCompleteTarget.checked = suggestions.receipt_invoice_complete
      this.receiptInvoiceCompleteTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  showIndicator() {
    if (this.hasIndicatorTarget) this.indicatorTarget.classList.remove("hidden")
    if (this.hasFieldsTarget) this.fieldsTarget.classList.add("hidden")
    this.toggleSubmit(true)
  }

  hideIndicator() {
    if (this.hasIndicatorTarget) this.indicatorTarget.classList.add("hidden")
    if (this.hasFieldsTarget) this.fieldsTarget.classList.remove("hidden")
    this.toggleSubmit(false)
  }

  toggleSubmit(disabled) {
    document
      .querySelectorAll('[data-submit-button-form-target="#offcanvas form"]')
      .forEach((btn) => {
        btn.disabled = disabled
        btn.classList.toggle("opacity-50", disabled)
        btn.classList.toggle("cursor-not-allowed", disabled)
      })
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }
}
